import React, { useCallback, useLayoutEffect, useRef, FunctionComponent } from 'react';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CarRejected from '../../../../Assets/Images/detailsCarRejected.png';
import { IDefaultJob } from '../../../../Shared/Interfaces';
import { ordersJobDateTimeLabel } from '../../ordersSurface';

interface Props {
    rejectedJob: IDefaultJob;
    onClose: () => void;
}

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableElements(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
        if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') return false;
        return element.tabIndex >= 0;
    });
}

function focusIfConnected(element: HTMLElement | null): void {
    if (element && document.contains(element)) {
        element.focus();
    }
}

const RejectedJobModal: FunctionComponent<Props> = (Props) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const backButtonRef = useRef<HTMLButtonElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const closingRef = useRef(false);
    const closeModalRef = useRef<() => void>(() => undefined);

    const closeModal = useCallback(() => {
        closingRef.current = true;
        focusIfConnected(previouslyFocusedRef.current);
        Props.onClose();
    }, [Props]);
    closeModalRef.current = closeModal;

    useLayoutEffect(() => {
        const active = document.activeElement;
        previouslyFocusedRef.current = active instanceof HTMLElement ? active : null;
        closingRef.current = false;

        const keepFocusInside = (shiftKey: boolean) => {
            const root = dialogRef.current;
            if (!root || closingRef.current) return;
            const focusable = focusableElements(root);
            if (focusable.length === 0) {
                root.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const activeElement = document.activeElement;
            const inside = activeElement instanceof Node && root.contains(activeElement);
            if (shiftKey) {
                if (!inside || activeElement === first) last.focus();
                return;
            }
            if (!inside || activeElement === last) first.focus();
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (closingRef.current) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                closeModalRef.current();
                return;
            }
            if (event.key !== 'Tab') return;
            const root = dialogRef.current;
            if (!root) return;
            const focusable = focusableElements(root);
            const activeElement = document.activeElement;
            const inside = activeElement instanceof Node && root.contains(activeElement);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const leaving =
                focusable.length === 0 ||
                !inside ||
                (event.shiftKey ? activeElement === first : activeElement === last);
            if (!leaving) return;
            event.preventDefault();
            keepFocusInside(event.shiftKey);
        };

        const onFocusIn = (event: FocusEvent) => {
            if (closingRef.current) return;
            const root = dialogRef.current;
            if (!root) return;
            const target = event.target;
            if (!(target instanceof Node) || root.contains(target)) return;
            const shiftKey =
                event.relatedTarget instanceof Node && root.contains(event.relatedTarget)
                    ? event.relatedTarget === focusableElements(root)[0]
                    : false;
            keepFocusInside(shiftKey);
        };

        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('focusin', onFocusIn, true);
        backButtonRef.current?.focus();

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('focusin', onFocusIn, true);
            closingRef.current = true;
            focusIfConnected(previouslyFocusedRef.current);
        };
    }, []);

    return (
        <div className="fixed w-full h-full top-0 left-0 flex z-50 ">
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="rejected-job-dialog-title"
                tabIndex={-1}
                className="fixed w-full overflow-y-scroll bg-details-background bg-cover pb-12"
                // eslint-disable-next-line react-native/no-inline-styles
                style={{ height: '100%' }}
            >
                <div className="fixed justify-start p-2 border-b border-solid border-gray-300 bg-gray-100 w-full">
                    <button
                        ref={backButtonRef}
                        className="text-blue-500 background-transparent font-bold text-sm outline-none focus:outline-none"
                        type="button"
                        onClick={closeModal}
                    >
                        <FontAwesomeIcon className="text-base mr-1" icon={faChevronLeft} />
                        <span>Back</span>
                    </button>
                </div>
                <div className="pt-14 container mx-auto h-auto text-left pb-4">
                    <div className="flex flex-col items-center">
                        <div id="rejected-job-dialog-title" className="uppercase text-xl font-medium">
                            {Props.rejectedJob.status} {Props.rejectedJob.type} Job
                        </div>
                        <div className="mt-6 text-3xl">
                            {Props.rejectedJob.vehicle?.year} {Props.rejectedJob.vehicle?.make}{' '}
                            {Props.rejectedJob.vehicle?.model}
                        </div>
                        <div className="mt-6 md:max-w-lg max-w-sm">
                            <img src={CarRejected} alt="Tech Scheduler Car" />
                        </div>
                        <div className="mt-4 text-base text-gray-600 text-center">
                            <p className="my-1">Insurance: {Props.rejectedJob.vehicle?.insurance}</p>
                            <p className="my-1">Contact: {Props.rejectedJob.contact}</p>
                            <p className="my-1">
                                Date: {Props.rejectedJob.date ? ordersJobDateTimeLabel(Props.rejectedJob) : ''}
                            </p>
                            <p className="my-1">Drop-off: {Props.rejectedJob.dropped}</p>
                        </div>
                        <div className="mt-4">
                            <button
                                type="button"
                                disabled
                                className="bg-gray-200 hover:bg-gray-200 text-white font-bold py-3 px-4 rounded w-72"
                            >
                                Job Rejected
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RejectedJobModal;
